import { Component, Inject, OnInit, Optional } from '@angular/core';
import { DateModel } from '@app/core/components/date-range/date-range.component';
import { Item } from '@app/core/components/listbox/listbox.component';
import { RememberService } from '@app/core/services/remember.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { SrExpensesGet, SrRefundService } from '@app/services/sr-refund.service';

@Component({
    selector: 'tt-sr-expenses',
    templateUrl: './sr-expenses.component.html',
    styleUrls: ['./sr-expenses.component.css'],
    standalone: false,
})
export class SrExpensesComponent implements OnInit {
    public page?: SrExpensesGet;
    public items: Item[] = [];
    public error = '';
    private initialized = false;
    private initTimer?: ReturnType<typeof setTimeout>;
    private listTimer?: ReturnType<typeof setTimeout>;
    private readonly rememberTimers = new Map<ExpenseRememberField, ReturnType<typeof setTimeout>>();
    private readonly intervals = ['day', 'week', 'month', 'quarter', 'biannual', 'year', 'yeartodate', 'minmax'];

    constructor(private readonly refund: SrRefundService, private readonly remember: RememberService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator) {
        this.navigator = useNavigatorFor(navigator, this);
    }

    public async ngOnInit(): Promise<void> {
        try {
            const page = await this.refund.getExpenses({
                argtype: String(this.navigator?.params['argtype'] ?? ''),
                argvalue: String(this.navigator?.params['argvalue'] ?? ''),
            });
            this.page = { ...page };
            clearTimeout(this.initTimer);
            this.initTimer = setTimeout(() => this.initialized = true, 50);
            this.loadItems();
        } catch (error) { this.showError(error); }
    }

    public onViewChanged(value: string): void {
        if (!this.page) return;
        const previous = this.page.bg_srexpenses_main;
        this.page.bg_srexpenses_main = value;
        if (!this.changed(value, previous)) return;
        if (this.initialized) this.rememberField('bg_srexpenses_main');
        this.loadItems();
    }

    public onSearchChanged(value: string): void {
        if (!this.page) return;
        const previous = this.page.searchtext;
        this.page.searchtext = value;
        if (this.changed(value, previous)) this.rememberField('searchtext');
    }

    public onDatesChanged(dates: DateModel): void {
        if (!this.page) return;
        const index = this.intervals.indexOf(dates.dateselector_index);
        const interval = index < 0 ? dates.dateselector_index : String(index + 1);
        const previous = this.page;
        this.page = { ...previous, ...dates, dateselector_index: interval };
        const fields = (['date_fom', 'date_tom', 'dateselector_index'] as const)
            .filter(field => this.changed(this.page![field], previous[field], true));
        if (fields.length === 0) return;
        this.page.date_fomtom = this.page.date_fom + this.page.date_tom;
        fields.forEach(field => this.rememberField(field));
        // An interval-only change reloads only if the concatenated dates also change.
        if (this.changed(this.page.date_fomtom, previous.date_fomtom)) this.loadItems();
    }

    public get filteredItems(): Item[] {
        const search = this.page?.searchtext ?? '';
        if (search === '') return this.items;
        const words = search.toLowerCase().split(' ');
        // Legacy filtering retains headers and rows without item_filtervalue, and removes duplicate row references.
        return this.items.filter((item, index, items) => items.indexOf(item) === index &&
            (item.item_id === '0' || item.item_id === '-1' || item.item_filtervalue === undefined ||
                words.every(word => item.item_filtervalue!.toLowerCase().includes(word))));
    }

    private loadItems(): void {
        clearTimeout(this.listTimer);
        const page = this.page;
        if (!page || page.searchtext === undefined || page.searchtext === null ||
            ![page.argtype, page.argvalue, page.bg_srexpenses_main, page.date_fom, page.date_tom, page.dateselector_index, page.date_fomtom]
                .every(value => typeof value === 'string' && value.trim().length > 0)) return;
        this.items = [];
        this.listTimer = setTimeout(() => {
            const current = this.page!;
            // Search is local; the legacy list endpoint receives only these seven fields.
            void this.refund.getExpenseList({ argtype: current.argtype, argvalue: current.argvalue,
                bg_srexpenses_main: current.bg_srexpenses_main, date_fom: current.date_fom, date_tom: current.date_tom,
                dateselector_index: current.dateselector_index, date_fomtom: current.date_fomtom })
                .then(items => this.items = items).catch(error => this.showError(error));
        }, 50);
    }

    private rememberField(field: ExpenseRememberField): void {
        clearTimeout(this.rememberTimers.get(field));
        this.rememberTimers.delete(field);
        const value = this.page![field];
        if (value === undefined || value === null || (field !== 'searchtext' && !value.trim())) return;
        const write = () => {
            this.rememberTimers.delete(field);
            void this.remember.remember(`w_srexpenses.${field}`, value).catch(error => this.showError(error));
        };
        // Explicit remembering preserves the conditional 500 ms delay for every changed filter.
        if (this.page!.searchtext > '') this.rememberTimers.set(field, setTimeout(write, 500));
        else write();
    }

    private changed(value: string | undefined, previous: string | undefined, limitLength = false): boolean {
        return value !== undefined && previous !== undefined && value !== previous &&
            (!limitLength || (value.length <= 16 && previous.length <= 16));
    }

    public openItem({ item, event }: { item: Item; event: MouseEvent }): void {
        if (item.item_state === 'customaction') {
            const action = item['item_action'];
            if (typeof action === 'function') action(item);
            return;
        }
        if (item.item_path === '' || !item.item_state) return;
        const target = { to: ['srexpenses', 'srtravelbill', 'srtravelbillspec', 'srreportmenu'].includes(item.item_state) ? `${item.item_state}-test` : item.item_state, parms: item.item_parms };
        if (event.ctrlKey) this.navigator?.newTab(target); else this.navigator?.go(target);
    }
    private showError(error: unknown): void { this.error = error instanceof Error ? error.message : String(error); }
}

// These are the only fields persisted by the legacy expense filters.
type ExpenseRememberField = 'bg_srexpenses_main' | 'searchtext' | 'date_fom' | 'date_tom' | 'dateselector_index';
